'use client';

import React, { useCallback, useEffect } from 'react';

import { css } from '@pt-group-fe/styled-system/css';
import { animated, useTransition } from '@react-spring/web';
import { useWindowScroll, useWindowSize } from 'react-use';

import { useIsMobile } from '@lib/hooks';

import EcosystemLastLayer from './assets/EcosystemLastLayer';
import EcosystemMiddleLayer from './assets/EcosystemMiddleLayer';
import EcosystemTopLayer from './assets/EcosystemTopLayer';

type ImageLayer = 1 | 2 | 3;

interface Props {
  setActiveLayer: React.Dispatch<React.SetStateAction<ImageLayer>>;
  activeLayer: ImageLayer;
}

const imageBox = css({
  pos: 'relative',
  minH: '280vh',

  '@media (max-width: 1000px)': {
    w: 'auto',
    display: 'inline-block'
  },

  '& img': {
    pos: 'sticky',
    top: { lg: '110px', xl: '160px' },
    maxH: 'calc(100vh - 180px)',
    mx: 'auto'
  }
});

const layerWrapper = css({
  pos: 'absolute',
  inset: 0
});

const EcosystemParallaxContainer: React.FC<Props> = ({ setActiveLayer, activeLayer }) => {
  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const ref = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wScroll = useWindowScroll();

  const calculateLayer = useCallback(() => {
    if (!ref.current || !containerRef.current || isMobile) return;

    const scrollStart = containerRef.current.getBoundingClientRect().top + wScroll.y;
    const scrollEnd = scrollStart + containerRef.current.getBoundingClientRect().height - 500;
    const layerSection = (scrollEnd - scrollStart) / 3;

    let layer: ImageLayer = 1;

    if (wScroll.y > scrollStart + layerSection) {
      layer = 2;
    }
    if (wScroll.y > scrollStart + 2 * layerSection) {
      layer = 3;
    }

    setActiveLayer(layer);
  }, [isMobile, setActiveLayer, wScroll.y]);

  useEffect(() => {
    calculateLayer();
  }, [wScroll, windowSize, calculateLayer]);

  const renderLayer = useCallback((layer: ImageLayer) => {
    if (layer === 1) return <EcosystemTopLayer />;
    if (layer === 2) return <EcosystemMiddleLayer />;

    return <EcosystemLastLayer />;
  }, []);

  const transitions = useTransition<ImageLayer, { opacity: number }>(activeLayer, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 200 }
  });

  return (
    <div className={imageBox} ref={containerRef}>
      <div ref={ref}>
        {transitions((style, layer) => {
          if (layer < 1 || layer > 3) return null;

          return (
            <animated.div className={layerWrapper} style={style}>
              {renderLayer(layer)}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

export default EcosystemParallaxContainer;
